/** Data health - *what do I do next* (ADR-0195, the second fixed view).
 *
 * What arrived and what did not (the census, in the states ADR-0166 keeps apart), how much of the
 * window can show its evidence (the six-link assessment, summarised by the server), how sure the
 * figures are (the grade mix per scope) and where documents are in the pipeline. Every mark opens
 * the screen that owns the thing it counts: Coverage, Figures or the evidence library.
 *
 * **A gap is a count of windows, never a cost.** What a missing month costs is a labelled note on
 * Coverage; a dashboard that turned it into tonnes would be a tile with a source of its own.
 */

import { Link } from "@tanstack/react-router";

import { useDocumentStageCounts } from "../../api/dashboardReads";
import { useInventory } from "../../api/queries";
import { useCoverage } from "../../api/sources";
import { quantity } from "../../lib/format";
import { chainOfEvidence } from "../readinessModel";
import type { Screen } from "../useScreen";
import { carryOf, censusBars, siteGaps, stageBars } from "./dashboardModel";
import styles from "./Dashboards.module.css";
import { BarRows } from "./parts";
import type { BarRow, Fill } from "./parts";
import { Tile } from "./Tile";

const CENSUS_FILL: Record<string, Fill> = {
  submitted: "info",
  declared_nil: "muted",
  pending: "muted",
  unknown: "warn",
  missing: "warn",
  not_expected: "muted",
};

export function DataHealthView({ screen }: { screen: Screen }) {
  const carry = carryOf(screen);
  const start = screen.window?.period_start;
  const end = screen.window?.period_end;
  const coverage = useCoverage(start, end);
  const inventory = useInventory(screen.window, screen.method, { evidence: true });
  const stages = useDocumentStageCounts(true);

  const census = coverage.data ? censusBars(coverage.data) : undefined;
  const gaps = coverage.data ? siteGaps(coverage.data) : [];
  const chain = inventory.data?.evidence ? chainOfEvidence(inventory.data.evidence) : undefined;

  return (
    <div className={styles.view} data-testid="dashboard-data-health">
      <div className={styles.span7}>
        <Tile
          id="health-census"
          title="What arrived"
          sub="Expected-bill periods in this period, in six states that stay apart"
          of="The coverage figures"
          reads={[coverage]}
          action={
            <Link to="/sources" search={{ ...carry, tab: "coverage" }} className={styles.link}>
              Coverage
            </Link>
          }
        >
          {census && census.expected === 0 ? (
            <p className={styles.empty}>
              No source is expected in this period yet. Declare what to expect on Coverage and this tile counts it.
            </p>
          ) : census ? (
            <BarRows
              testId="dash-census"
              caption="Expected-bill periods by state"
              head={["State", "Periods"]}
              rows={census.bars.map(
                (bar): BarRow => ({
                  id: bar.key,
                  name: bar.label,
                  fraction: bar.fraction,
                  figure: quantity(String(bar.count)),
                  fill: CENSUS_FILL[bar.key] ?? "muted",
                  state: bar.count === 0 ? "outline" : "drawn",
                  // The state the bar counted, so Coverage lands on the streams behind it
                  // (`sourceNarrowing.ts`); the keys of `censusBars` are the census's own states.
                  to: { to: "/sources", search: { ...carry, tab: "coverage", state: bar.key } },
                  openLabel: `${bar.label}: ${bar.count} ${bar.count === 1 ? "window" : "windows"}. Open Coverage.`,
                }),
              )}
            />
          ) : null}
          {census ? (
            <p className={styles.note}>
              A period that has not closed is waiting, not missing. A source that may have had nothing
              to send is &ldquo;cannot tell&rdquo;, never counted as sent.
            </p>
          ) : null}
        </Tile>
      </div>

      <div className={styles.span5}>
        <Tile
          id="health-gaps"
          title="Where the gaps are"
          sub="Sites with a period that is missing or cannot be told"
          of="The coverage figures"
          reads={[coverage]}
        >
          {gaps.length === 0 ? (
            <p className={styles.empty}>No site has a bill missing or that cannot be told.</p>
          ) : (
            <BarRows
              testId="dash-gaps"
              caption="Sites with bills missing or that cannot be told"
              head={["Site", "Periods"]}
              rows={gaps.map((gap): BarRow => {
                const total = gap.missing + gap.unknown;
                const top = gaps[0] ? gaps[0].missing + gaps[0].unknown : 1;
                return {
                  id: gap.facilityId,
                  name: gap.name,
                  sub: `${gap.missing} missing, ${gap.unknown} cannot be told`,
                  fraction: top > 0 ? total / top : 0,
                  figure: String(total),
                  fill: "warn",
                  // The site the row counted (a facility id, the register's own `site` filter).
                  to: { to: "/sources", search: { ...carry, tab: "coverage", site: gap.facilityId } },
                  openLabel: `${gap.name}: ${total} bills to resolve. Open Coverage.`,
                };
              })}
            />
          )}
        </Tile>
      </div>

      <div className={styles.span6}>
        <Tile
          id="health-evidence"
          title="Can each figure show its evidence"
          /* **Says what "complete" was checked against, and only says "every" when it is.** The claim
             is the server's six-link assessment of the whole window; it is never inferred from a
             non-empty column (F10), so the sentence names the two ends of the chain it rests on. */
          sub={
            chain && chain.total > 0 && chain.complete === chain.total
              ? "Every figure reaches a stored, verified document and a ledger entry"
              : "EmitIQ checks each figure back to its document, over the whole window"
          }
          of="The evidence check"
          reads={[inventory]}
          action={
            // The figures behind the tally: the ones with a broken link when there are any, else the
            // whole window (what the old home screen's Traceable drill did, now where the claim is).
            <Link
              to="/emissions"
              search={{ ...carry, tab: "register", status: chain && chain.broken > 0 ? "broken" : "complete" }}
              className={styles.link}
              data-testid="drill-traceable"
            >
              Figures
            </Link>
          }
        >
          {chain ? (
            <>
              <div className={styles.tally} data-testid="dash-chain">
                <div className={styles.tallyCell}>
                  <strong>{quantity(String(chain.complete))}</strong>
                  <span>of {quantity(String(chain.total))} complete</span>
                </div>
                <div className={styles.tallyCell}>
                  <strong>{quantity(String(chain.broken))}</strong>
                  <span>with evidence missing</span>
                </div>
                <div className={styles.tallyCell}>
                  <strong>{quantity(String(chain.unchecked))}</strong>
                  <span>not checked</span>
                </div>
              </div>
              {inventory.data?.evidence?.commonest_break_why ? (
                <p className={styles.note}>Commonest break: {inventory.data.evidence.commonest_break_why}</p>
              ) : null}
            </>
          ) : (
            <p className={styles.empty}>EmitIQ did not summarise the evidence for this window.</p>
          )}
        </Tile>
      </div>

      <div className={styles.span6}>
        <Tile
          id="health-grades"
          title="How sure the figures are"
          sub="Share of each scope's figures graded A"
          of="The scope totals"
          reads={[inventory]}
        >
          {inventory.data ? (
            <BarRows
              testId="dash-grades"
              caption="Figures graded A, by scope"
              head={["Scope", "Graded A"]}
              rows={inventory.data.totals_by_scope.map((total): BarRow => {
                const figures = total.figures ?? 0;
                const graded = total.graded_a ?? 0;
                return !total.assessed
                  ? {
                      id: `scope-${total.scope}`,
                      name: `Scope ${total.scope}`,
                      fraction: 0,
                      figure: "Not assessed",
                      state: "outline",
                    }
                  : {
                      id: `scope-${total.scope}`,
                      name: `Scope ${total.scope}`,
                      sub: figures === 0 ? "no figures" : `${quantity(String(graded))} of ${quantity(String(figures))} figures`,
                      fraction: figures > 0 ? graded / figures : 0,
                      figure: figures > 0 ? `${Math.round((graded / figures) * 100)}%` : "none",
                      fill: total.scope === "1" ? "scope1" : total.scope === "2" ? "scope2" : "info",
                      to: { to: "/emissions", search: { ...carry, scope: total.scope } },
                      openLabel: `Scope ${total.scope}: ${quantity(String(graded))} of ${quantity(String(figures))} figures graded A. Open the figures.`,
                    };
              })}
            />
          ) : null}
          <p className={styles.note}>
            A grade says how a figure was measured, with its reason on the receipt. A figure that
            rests on an estimate is below A; replacing it with a record lifts the grade.
          </p>
        </Tile>
      </div>

      <div className={styles.span12}>
        <Tile
          id="health-stages"
          title="Documents by stage"
          sub="Where each document in the library has got to"
          of="The document stages"
          reads={[stages]}
          action={
            <Link to="/evidence" search={carry} className={styles.link}>
              Upload &amp; feeds
            </Link>
          }
        >
          {stages.data && stages.data.documents === 0 ? (
            <p className={styles.empty}>No documents have been uploaded to this workspace.</p>
          ) : stages.data ? (
            <BarRows
              testId="dash-stages"
              caption="Documents by stage"
              head={["Stage", "Documents"]}
              rows={stageBars(stages.data).map(
                (bar): BarRow => ({
                  id: bar.stage,
                  name: bar.label,
                  sub: bar.meaning,
                  fraction: bar.fraction,
                  figure: quantity(String(bar.count)),
                  fill: bar.stage.includes("review") || bar.stage.includes("quarant") ? "warn" : "info",
                  state: bar.count === 0 ? "outline" : "drawn",
                  to: { to: "/evidence", search: carry },
                  openLabel: `${bar.label}: ${bar.count} documents. Open the library.`,
                }),
              )}
            />
          ) : null}
        </Tile>
      </div>
    </div>
  );
}
